package pl.decks.personal.pages.ievp.sections.skills

import androidx.compose.runtime.*
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.silk.components.forms.Button
import com.varabyte.kobweb.silk.components.icons.fa.FaBolt
import com.varabyte.kobweb.silk.components.icons.fa.FaChevronDown
import com.varabyte.kobweb.silk.components.icons.fa.FaChevronUp
import com.varabyte.kobweb.silk.components.icons.fa.FaStar
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import pl.decks.personal.components.widgets.LeadingIconRow
import pl.decks.personal.components.widgets.TrailingIconRow
import pl.decks.personal.components.widgets.button.ButtonStylesheet
import pl.decks.personal.components.widgets.disclaimer.DisclaimerBox
import pl.decks.personal.components.widgets.iconribbon.IconRibbon
import pl.decks.personal.components.widgets.iconribbon.IconRibbonOption
import pl.decks.personal.components.widgets.iconribbon.IconRibbonVariant
import pl.decks.personal.components.widgets.mode.ModeInput
import pl.decks.personal.components.widgets.mode.ModeInputOption
import pl.decks.personal.components.widgets.progression.RampingProgressionBar
import pl.decks.personal.components.widgets.ranks.Rank
import pl.decks.personal.components.widgets.ranks.RanksPreview
import pl.decks.personal.components.widgets.ranks.RanksStylesheet
import pl.decks.personal.components.widgets.ranks.SlotRank
import pl.decks.personal.components.widgets.responsivegrid.ResponsiveGrid
import pl.decks.personal.components.widgets.responsivegrid.ResponsiveGridOption
import pl.decks.personal.extensions.responsiveValue
import pl.decks.personal.extensions.toSitePalette
import pl.decks.personal.pages.ievp.IEVPJourney
import pl.decks.personal.pages.ievp.IEVPSection
import pl.decks.personal.pages.ievp.sections.profile.ProfileDimension
import pl.decks.personal.pages.ievp.sections.skills.SkillsSectionStylesheet.ProfileDimensions
import pl.decks.personal.pages.ievp.sections.skills.SkillsSectionStylesheet.RanksSlotsContainer
import pl.decks.personal.pages.ievp.sections.skills.SkillsSectionStylesheet.SystemSelection
import pl.decks.personal.pages.ievp.sections.skills.SkillsSectionStylesheet.Vars
import pl.decks.personal.styles.FontTypography
import pl.decks.personal.styles.SiteLayout
import pl.decks.personal.theme.SitePalette
import pl.decks.posthog.client.PostHogClient
import kotlin.math.pow

@Composable
internal fun SkillsSectionContent(headingProgress: Float) =
    Column(
        SkillsSectionStylesheet.Page.toModifier()
            .opacity(headingProgress.pow(6).times(100).toInt().percent)
    ) {
        var selectedSystem by remember { mutableStateOf(RankingSystem.entries.first()) }
        DisclaimerBox(
            modifier = Modifier
                .fillMaxWidth()
                .fontSize(FontTypography.Size.Small)
                .margin(topBottom = SiteLayout.Space.Medium)
                .onClick { PostHogClient.capture(IEVPJourney.DisclaimerOpened(IEVPSection.SKILLS)) },
        ) {
            SpanText("There is no predefined system for where each skill lies. " +
                    "I would be surprised if there were no disagreements over their division and placement. " +
                    "And that is okay - at least there is something to discuss.")
        }
        RankingSystemSelection(
            selectedSystem, onSystemSelected = {
                selectedSystem = it
                PostHogClient.capture(IEVPJourney.RankingSystemSwitched(selectedSystem))
            }
        )
        if (headingProgress > 0.3f) {
            ProfileDimensionsPreview(selectedSystem)
        }
    }

@Composable
private fun RankingSystemSelection(
    selectedSystem: RankingSystem,
    onSystemSelected: (RankingSystem) -> Unit,
) = Column(
    modifier = SystemSelection.Style.toModifier(),
    horizontalAlignment = Alignment.CenterHorizontally,
) {
    SpanText("Rank the skills as you like!", modifier = SystemSelection.Title.toModifier())
    Column(Modifier.fillMaxWidth()) {
        RankingSystemModeInput(selectedSystem, onSystemSelected)
        RankingSystemSlotsPresentation(selectedSystem)
    }
}

@Composable
private fun RankingSystemModeInput(
    selectedSystem: RankingSystem,
    onSystemSelected: (RankingSystem) -> Unit
) = ModeInput(
    modifier = SystemSelection.ModeInput.toModifier()
        .borderRadius(
            // Must set inline to override ModeInput defaults
            topLeft = Vars.systemSelectionBorderRadius.value(),
            topRight = Vars.systemSelectionBorderRadius.value(),
        ),
    options = RankingSystem.entries.map { sys ->
        val isSelected = sys == selectedSystem
        ModeInputOption(
            isSelected = isSelected,
            onModeClicked = { if (!isSelected) onSystemSelected(sys) },
            modifier = SystemSelection.InputOption.toModifier(
                if (isSelected) SystemSelection.InputOptionSelectedVariant
                else SystemSelection.InputOptionOtherVariant
            ),
        ) {
            when (sys) {
                RankingSystem.PARA_MILITARY -> LeadingIconRow(icon = { FaStar() }) { SpanText("Para military") }
                RankingSystem.BATTERY -> TrailingIconRow(icon = { FaBolt() }) { SpanText("Charging") }
            }
        }
    },
)

@Composable
private fun RankingSystemSlotsPresentation(system: RankingSystem) {
    var isExpanded by remember { mutableStateOf(false) }
    Column(SystemSelection.SlotsPresentation.toModifier()) {
        Box(
            modifier = RanksSlotsContainer.Style.toModifier(
                if (isExpanded) RanksSlotsContainer.ExpandedVariant else RanksSlotsContainer.CollapsedVariant,
            )
        ) {
            key(system) { RanksPreview(system.ranks, modifier = Modifier.fillMaxWidth()) }
        }
        RankingSystemSlotsExpander(
            isExpanded, onExpandToggled = {
                isExpanded = !isExpanded
                if (isExpanded) PostHogClient.capture(IEVPJourney.RankingPreviewed)
            }
        )
    }
}

@Composable
private fun RankingSystemSlotsExpander(
    isExpanded: Boolean,
    onExpandToggled: () -> Unit,
) = Button(
    onClick = { onExpandToggled() },
    modifier = Modifier.fillMaxWidth().borderRadius(SiteLayout.Fraction.none),
    variant = ButtonStylesheet.GhostVariant,
) {
    Column(horizontalAlignment = Alignment.CenterHorizontally) {
        if (isExpanded) {
            FaChevronUp()
            SpanText("Collapse", modifier = RanksStylesheet.InstructionText.toModifier())
        } else {
            SpanText("Preview ranking system", modifier = RanksStylesheet.InstructionText.toModifier())
            FaChevronDown()
        }
    }
}

@Composable
private fun ProfileDimensionsPreview(system: RankingSystem) {
    var dimension by remember { mutableStateOf(ProfileDimension.entries.first()) }
    var category by remember(dimension) { mutableStateOf(dimension.categories.first()) }

    LaunchedEffect(dimension, category) {
        PostHogClient.capture(IEVPJourney.SkillCategoryPresented(dimension, category))
    }

    Column(
        modifier = ProfileDimensions.Preview.toModifier()
            .setVariable(Vars.profileDimensionColor, dimension.color(ColorMode.current.toSitePalette()))
    ) {
        ProfileDimensionSelector(dimension, onDimensionSelected = { dimension = it })

        ProfileDimensionSkillCategorySelection(
            selectedCategory = category,
            dimensionCategories = dimension.categories,
            onCategorySelected = { category = it },
        )

        ProfileDimensionCategoryPreview(
            rank = system.get(category.rankLevel),
            skills = category.skills,
        )
    }
}

@Composable
private fun ProfileDimensionSelector(
    selectedDimension: ProfileDimension,
    onDimensionSelected: (ProfileDimension) -> Unit,
) = IconRibbon(
    options = ProfileDimension.entries.map { it.toRibbonOption(ColorMode.current.toSitePalette()) },
    selectedOption = selectedDimension.ordinal,
    onOptionSelected = { onDimensionSelected(ProfileDimension.entries[it]) },
    initialVariant = IconRibbonVariant.EXPANDED,
    ribbonBackgroundColor = ColorMode.current.toSitePalette().surface,
    modifier = Modifier.fillMaxWidth(),
)

@Composable
private fun ProfileDimensionSkillCategorySelection(
    selectedCategory: SkillCategory,
    dimensionCategories: List<SkillCategory>,
    onCategorySelected: (SkillCategory) -> Unit,
) = ResponsiveGrid(
    options = dimensionCategories.map { ResponsiveGridOption(title = it.title) },
    selectedOption = dimensionCategories.indexOf(selectedCategory),
    numColumns = responsiveValue(2, md = dimensionCategories.size),
    onOptionSelected = { onCategorySelected(dimensionCategories[it]) },
    hoverGrow = responsiveValue(1.5f, md = 1.2f),
    modifier = Modifier.fillMaxWidth(),
)

@Composable
private fun ProfileDimensionCategoryPreview(
    rank: Rank,
    skills: List<SkillCategory.Skill>,
) = Row(
    modifier = ProfileDimensions.Category.toModifier(),
    verticalAlignment = Alignment.CenterVertically,
) {
    Box(Modifier.height(165.px).flexShrink(0)) {
        SlotRank(rank)
    }
    Column(ProfileDimensions.Skills.toModifier().weight(1f)) {
        skills.forEach { skill ->
            key(skill) {
                ProfileDimensionCategorySkillBar(skill.name, skill.ranking)
            }
        }
    }
}

@Composable
private fun ProfileDimensionCategorySkillBar(
    name: String,
    ranking: Int,
) = Column(ProfileDimensions.SkillBar.toModifier()) {
    SpanText(name, modifier = ProfileDimensions.SkillTitle.toModifier())
    RampingProgressionBar(
        ranking.toFloat(),
        modifier = Modifier.fillMaxWidth(),
        formatter = { v, _, _ -> "${v.toInt()}%" }
    )
}

private fun ProfileDimension.toRibbonOption(sitePalette: SitePalette, iconModifier: Modifier = Modifier) =
    IconRibbonOption(
        title = this.title,
        color = this.color(sitePalette),
        icon = { this.icon(iconModifier) }
    )
